import { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '@leadgab/shared';
import type { SessionType } from '@leadgab/shared';
import { ApiRequestError, uploadAttachments } from '../lib/api.ts';
import type { AttachmentDto } from '../lib/api.ts';
import { ImageIcon, PaperclipIcon, SendIcon, XIcon } from './icons.tsx';
import { Spinner } from './ui.tsx';

interface Preset {
  id: number;
  title: string;
  content: string;
  shortcut: string | null;
}

interface ComposerProps {
  disabled: boolean;
  disabledReason?: string;
  sending: boolean;
  sessionType: SessionType;
  presets: Preset[];
  sendWithEnter: boolean;
  /** False when no Twilio number is connected, which takes SMS off the table. */
  smsConfigured: boolean;
  /** Resolves when the message is confirmed sent; rejects so the draft is kept. */
  onSend: (
    content: string,
    attachments: AttachmentDto[],
    prefer?: 'sms' | 'email',
  ) => Promise<unknown>;
}

/** An image the agent picked, tracked from selection through upload. */
interface PendingAttachment {
  id: string;
  /** Object URL, so the thumbnail appears before the upload finishes. */
  previewUrl: string;
  name: string;
  uploaded?: AttachmentDto;
  error?: string;
}

let attachmentCounter = 0;

export function Composer({
  disabled,
  disabledReason,
  sending,
  sessionType,
  presets,
  sendWithEnter,
  smsConfigured,
  onSend,
}: ComposerProps): React.JSX.Element {
  const [value, setValue] = useState('');
  const [prefer, setPrefer] = useState<'sms' | 'email' | undefined>(undefined);
  const [showPresets, setShowPresets] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const channels: readonly ('sms' | 'email')[] = smsConfigured ? ['sms', 'email'] : ['email'];

  // Derived rather than stored, so a number disconnected while this composer is
  // open cannot leave a stale choice behind that the next send acts on.
  const chosen = prefer && channels.includes(prefer) ? prefer : undefined;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Grow with the content, but stop before the composer eats the transcript.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;

    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
  }, [value]);

  // Object URLs are held by the browser until explicitly released.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  useEffect(
    () => () => {
      for (const attachment of attachmentsRef.current) URL.revokeObjectURL(attachment.previewUrl);
    },
    [],
  );

  /**
   * Adds images and starts their uploads.
   *
   * The side effects — creating object URLs and firing the POST — stay outside
   * the state updater. React may invoke an updater more than once for a single
   * update (it does in StrictMode), which would upload every attachment twice
   * and strand the object URL from the discarded pass.
   */
  const addFiles = useCallback((files: File[]): void => {
    const images = files.filter((file) => file.type.startsWith('image/'));
    const room = MAX_ATTACHMENTS_PER_MESSAGE - attachmentsRef.current.length;

    if (images.length === 0 || room <= 0) return;

    const accepted = images.slice(0, room);

    const pending: PendingAttachment[] = accepted.map((file) => {
      attachmentCounter += 1;
      return {
        id: `a${String(attachmentCounter)}`,
        previewUrl: URL.createObjectURL(file),
        name: file.name,
      };
    });

    // Kept in step by hand so two picks in the same tick still see the real
    // count; the render assignment below writes the same value back.
    attachmentsRef.current = [...attachmentsRef.current, ...pending];
    setAttachments(attachmentsRef.current);

    for (const [index, item] of pending.entries()) {
      const file = accepted[index] as File;

      void uploadAttachments([file])
        .then(([uploaded]) => {
          setAttachments((current) =>
            current.map((entry) => (entry.id === item.id ? { ...entry, uploaded } : entry)),
          );
        })
        .catch((error: unknown) => {
          const message = error instanceof ApiRequestError ? error.message : 'Upload failed';

          setAttachments((current) =>
            current.map((entry) => (entry.id === item.id ? { ...entry, error: message } : entry)),
          );
        });
    }
  }, []);

  const removeAttachment = (id: string): void => {
    setAttachments((previous) => {
      const target = previous.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);

      return previous.filter((item) => item.id !== id);
    });
  };

  const uploading = attachments.some((item) => !item.uploaded && !item.error);
  const ready = attachments.filter((item) => item.uploaded).map((item) => item.uploaded as AttachmentDto);
  const hasContent = value.trim().length > 0 || ready.length > 0;

  const submit = (): void => {
    // Sending mid-upload would silently drop the images still in flight.
    if (disabled || sending || uploading || !hasContent) return;

    const snapshot = attachments;
    setSendError(null);

    // Clear only once the send is confirmed. Resetting first meant a failed
    // request left the agent with an empty composer, revoked previews and no
    // message anywhere — nothing to retry and nothing to copy back.
    void onSend(value.trim(), ready, chosen)
      .then(() => {
        for (const attachment of snapshot) URL.revokeObjectURL(attachment.previewUrl);

        setValue('');
        setAttachments([]);
        attachmentsRef.current = [];
        setPrefer(undefined);
      })
      .catch((error: unknown) => {
        setSendError(
          error instanceof ApiRequestError ? error.message : 'The message was not sent. Try again.',
        );
      })
      .finally(() => {
        textareaRef.current?.focus();
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter') return;

    // With "send with Enter" on, Shift+Enter still inserts a newline; with it
    // off, the modifier is what sends.
    const shouldSend = sendWithEnter ? !event.shiftKey : event.metaKey || event.ctrlKey;

    if (shouldSend) {
      event.preventDefault();
      submit();
    }
  };

  /** Screenshots arrive as clipboard files, which is how most images get sent. */
  const handlePaste = (event: React.ClipboardEvent): void => {
    const files = [...event.clipboardData.files];
    if (files.length === 0) return;

    event.preventDefault();
    addFiles(files);
  };

  const applyPreset = (preset: Preset): void => {
    setValue((previous) => (previous ? `${previous.trimEnd()} ${preset.content}` : preset.content));
    setShowPresets(false);
    textareaRef.current?.focus();
  };

  if (disabled) {
    return (
      <div className="border-t border-slate-200 bg-white px-4 py-4 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
        {disabledReason ?? 'You cannot reply to this conversation.'}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'relative border-t border-slate-200 bg-white px-4 py-3 transition dark:border-slate-800 dark:bg-slate-900',
        dragging && 'ring-2 ring-brand-500 ring-inset',
      )}
      onDragOver={(event) => {
        // Only react to a file drag, not to text selected on the page.
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragging(false);
        addFiles([...event.dataTransfer.files]);
      }}
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-brand-50/90 text-sm font-medium text-brand-700 dark:bg-brand-950/80 dark:text-brand-200">
          <ImageIcon className="mr-2 h-5 w-5" />
          Drop images to attach
        </div>
      ) : null}

      {showPresets && presets.length > 0 ? (
        <div className="absolute bottom-full left-4 z-20 mb-2 w-80 max-w-[calc(100%-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <ul className="lg-scroll max-h-64 overflow-y-auto py-1">
            {presets.map((preset) => (
              <li key={preset.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => {
                    applyPreset(preset);
                  }}
                >
                  <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                    {preset.title}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                    {preset.content}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sendError ? (
        <p
          role="alert"
          className="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-950/60 dark:text-rose-200"
        >
          {sendError}
        </p>
      ) : null}

      {attachments.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700"
            >
              <img
                src={attachment.previewUrl}
                alt={attachment.name}
                className="h-full w-full object-cover"
              />

              {!attachment.uploaded && !attachment.error ? (
                <span className="absolute inset-0 flex items-center justify-center bg-slate-900/50 text-white">
                  <Spinner className="h-4 w-4" />
                </span>
              ) : null}

              {attachment.error ? (
                <span
                  className="absolute inset-0 flex items-center justify-center bg-rose-600/80 px-1 text-center text-[10px] leading-tight text-white"
                  title={attachment.error}
                >
                  {attachment.error}
                </span>
              ) : null}

              <button
                type="button"
                className="absolute top-0.5 right-0.5 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-slate-900"
                onClick={() => {
                  removeAttachment(attachment.id);
                }}
              >
                <XIcon className="h-3 w-3" />
                <span className="sr-only">Remove {attachment.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            addFiles([...(event.target.files ?? [])]);
            // Reset so picking the same file twice in a row still fires.
            event.target.value = '';
          }}
        />

        <button
          type="button"
          className="lg-btn-ghost shrink-0 px-2.5 py-2.5"
          title="Attach images"
          disabled={attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          <PaperclipIcon className="h-4 w-4" />
          <span className="sr-only">Attach images</span>
        </button>

        <textarea
          ref={textareaRef}
          rows={1}
          className="lg-input max-h-[200px] resize-none py-2.5"
          placeholder={
            sendWithEnter ? 'Write a reply — Enter to send' : 'Write a reply — ⌘/Ctrl+Enter to send'
          }
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />

        <button
          type="button"
          className="lg-btn-primary shrink-0 px-3"
          disabled={sending || uploading || !hasContent}
          onClick={submit}
          title={uploading ? 'Waiting for the upload to finish' : 'Send'}
        >
          {sending || uploading ? <Spinner /> : <SendIcon className="h-4 w-4" />}
          <span className="sr-only">Send</span>
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {presets.length > 0 ? (
          <button
            type="button"
            className={clsx(
              'rounded-md px-2 py-1 text-xs font-medium transition',
              showPresets
                ? 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100'
                : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800',
            )}
            onClick={() => {
              setShowPresets((open) => !open);
            }}
          >
            Preset replies
          </button>
        ) : null}

        {/*
          Only worth offering where a second channel exists: a Messenger thread
          has no email or number to fall back to, and SMS is not a channel at
          all until the workspace connects a Twilio number — offering it then
          would let an agent pick a route their reply cannot take.
        */}
        {sessionType !== 'fmp' ? (
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <span>Send offline replies via</span>
            {channels.map((channel) => (
              <button
                key={channel}
                type="button"
                className={clsx(
                  'rounded px-1.5 py-0.5 font-medium transition',
                  chosen === channel
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-200'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
                onClick={() => {
                  setPrefer((current) => (current === channel ? undefined : channel));
                }}
              >
                {channel.toUpperCase()}
              </button>
            ))}
            {chosen === undefined ? <span>· auto</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
